<template>
  <div class="goods-page-container">
    <div class="goods-page" ref="goodsPagRef">
      <Skeleton :loading="state.loading">
        <div class="container" v-if="goods?.id">
          <!-- 商品信息 -->
          <div class="goods-info" ref="goodsInfoRef">
            <div class="media">
              <GoodsImage
                :images="goods?.images"
                :goodsId="goods?.id"
                :isStar="state.isStar"
                :photosList="state.photosList"
                v-model:currentImg="state.currentImg"
                ref="goodsImageRef"
                @shareClick="shareClickFun"
                @collectGoodsClick="collectGoodsClickFun"
              />
            </div>
            <div
              class="scrollbar"
              :class="{
                'goods-content': !state.isFloat,
                'float-goods-content': state.isFloat,
                'animate-fade-in': state.isFloat,
              }"
            >
              <GoodsName
                v-model:goods="goods"
                v-model:currSku="state.currSku"
                :currentImg="state.currentImg"
                :isComplete="state.isCompete"
                @refreshData="getProductData"
              />
              <div class="sku-content scrollbar">
                <!-- 购买运费提示 -->
                <GoodsBuyTips
                  :isFloat="state.isFloat"
                  v-model:value="state.postage"
                  :postageLoad="state.postageLoad"
                  @postageChange="postageChangeCallback"
                />
                <GoodsSku
                  :goods="goods"
                  @change="changeSku"
                  @setPreviewImage="setPreviewImage"
                  @buyAction="buyAction"
                />
                <!-- 数量选择组件 -->
                <GoodsNum
                  :isFloat="state.isFloat"
                  :goods="goods"
                  v-model:value="num"
                  :currSku="state.currSku"
                  @change="getProductPostage"
                />
                <GoodsRemark v-if="!state.isFloat" v-model:value="state.goodsComment" />
                <SingleGoodsData
                  v-if="!state.isFloat"
                  :saleCount="goods.saleCount"
                  :weight="state.weight"
                  :volume="state.volume"
                  :receiveDays="state.receiveDays"
                />
                <ServiceAgree>
                  <!--  是否同意阅读-->
                  <div class="read-agree">
                    <el-checkbox
                      :label="$t('order.readAgreeText')"
                      v-model="state.isRead"
                      @change="checkAgree"
                    />
                  </div>
                </ServiceAgree>
              </div>
              <!-- 按钮组件 -->
              <div class="btn-list">
                <el-button
                  :loading="state.actionLoad"
                  @click="handleNowBuy"
                  :class="{
                    'buy-btn': true,
                    'active-btn': state.isCompete && state.isRead,
                  }"
                  :type="state.isCompete && state.isRead ? 'primary' : 'success'"
                  size="large"
                >
                  {{ $t('product.nowBuy') }}
                </el-button>
                <el-button
                  :loading="state.actionLoad"
                  class="add-btn"
                  @click="insertCart"
                  type="primary"
                  plain
                  size="large"
                >
                  {{ $t('product.addCart') }}
                </el-button>
                <div class="min-num-err" v-if="state.showMinNumErr">
                  {{
                    $t('product.nimNumTips', {
                      num: goods.startQuantity || '--',
                    })
                  }}
                </div>
              </div>
            </div>
          </div>
          <HistoryList v-if="userStore.isLogin" />
          <div class="goods-footer" ref="goodsFooterRef">
            <!-- 商品+评价 -->
            <GoodsTabs />
          </div>
        </div>
      </Skeleton>
      <ShareAffiliateDialog
        ref="shareAffiliateDialogRef"
        :linkUrl="state.shareLink"
      ></ShareAffiliateDialog>
      <VerifyEmailModal ref="verifyEmailRef"></VerifyEmailModal>
      <RiskReminderDialog :goods="goods" ref="riskReminderDialogRef"></RiskReminderDialog>
    </div>
  </div>
</template>

<script setup>
import {
  collectAdd,
  collectExists,
  collectRemove,
  getItemLogisticsData,
  getMeasureBySkuId,
  getProductDetail,
  getProductPostFee,
  getProductShareLink,
  goodsPhotos,
  nowBuyProduct,
} from '@/apis/goods'
import { addUserBrowsingHistory } from '@/apis/user'
import { sourceStoreMap } from '@/config/constant'
import VerifyEmailModal from '@/pages/member/accountInfo/AccountSafe/VerifyEmailModal'
import { useCartStore, useOrderStore, useUserStore } from '@/stores'
import { flatRateOrigin, mulFlatRate } from '@/utils/formatMoney'
import { debounce, throttle } from '@/utils/util'
import { useWindowScroll, useWindowSize } from '@vueuse/core'
import { ElMessage } from 'element-plus'
import { computed, nextTick, onMounted, provide, reactive, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import GoodsImage from './components/goods-image'
import GoodsName from './components/goods-name'
import GoodsNum from './components/goods-num'
import GoodsSku from './components/goods-sku'
import GoodsTabs from './components/goods-tabs'
import GoodsBuyTips from './components/GoodsBuyTips.vue'
import GoodsRemark from './components/GoodsRemark.vue'
import HistoryList from './components/HistoryList.vue'
import RiskReminderDialog from './components/riskReminderDialog.vue'
import ServiceAgree from './components/ServiceAgree.vue'
import ShareAffiliateDialog from './components/ShareAffiliateDialog.vue'
import SingleGoodsData from './components/SingleGoodsData.vue'
import Skeleton from './components/Skeleton.vue'

const { t } = useI18n()
const { y } = useWindowScroll()
const { width } = useWindowSize()
const $route = useRoute()
const goodsInfoRef = ref()
const goodsPagRef = ref()
const goodsFooterRef = ref()
const goodsImageRef = ref()
const shareAffiliateDialogRef = ref()
const verifyEmailRef = ref()
const riskReminderDialogRef = ref()
const cartStore = useCartStore()
const orderStore = useOrderStore()
const userStore = useUserStore()
const state = reactive({
  isCompete: false, // 是否完整规格
  loading: true,
  currSku: {}, // 当前sku
  goodsComment: '',
  shareLink: '',
  price: 0, //
  promotionPrice: 0,
  goodsInfoHeight: 0,
  pageOffsetLeft: 0,
  currentImg: undefined,
  postage: 0.0, // 邮费 页面显示
  postageCny: 0, // 邮费 人民币
  postageLoad: false,
  isFloat: false, // 下单是否浮动
  isRead: userStore.profile.disclaimerAgree, // 是否阅读协议
  againShowAgree: userStore.profile.againShowAgree, // 是否不再弹出协议
  type: $route.query.type || 'buy', // buy 立即够买 add 加入购物车
  showMinNumErr: false, // 是否显示最小起批量提示
  actionLoad: false, // 操作 loading
  noSku: false, // 没有sku
  isStar: false,
  photosList: [],
  weight: undefined,
  volume: undefined,
  receiveDays: 0,
})

const $router = useRouter()
const goods = shallowRef(null)
const route = useRoute()
const getProductData = async (forceFlush) => {
  state.loading = true
  let res
  try {
    res = await getProductDetail({
      forceFlush,
      itemId: route.query.id,
      source: route.query.source,
    })
    if (res.shopId === 'null') {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const CryptoJS = require('crypto-js')
      const data = `${Date.now()}-${Math.random() * 1000000}`
      res.shopId = CryptoJS.MD5(data).toString()
    }
  } catch (error) {
    // ElMessage({ type: 'error', message: error });
    console.log(error, 'error====')
    $router.push({ name: 'uni-order' })
  } finally {
    state.noSku = false
    state.currSku = {}
    state.promotionPrice = state.price = 0
    state.isCompete = false
    goods.value = res
    num.value = 1
    if (goods.value?.id) {
      getMeasureBySkuIdFun()
      collectExistsFun()
      getGoodsPhotos()
      getItemLogisticsDataFun()

      // 重新获取商品

      if (goods.value.blackStore) {
        riskReminderDialogRef.value.openModal(1)
      } else {
        if (goods.value.riskCheck) {
          if (goods.value.action === 0) {
            riskReminderDialogRef.value.openModal(2)
          } else if (goods.value.action === 1) {
            riskReminderDialogRef.value.openModal(3)
          }
        }
      }
      res.skuList = integrateProductTypeData(res.skus, res)
      // 需要默认展示价格
      if (!state.currSku.skuId) {
        state.currSku = {
          price: state.price,
          writePrice: state.promotionPrice || state.price,
          promotionPrice: state.promotionPrice,
        }
      }
      if (userStore.isLogin) {
        addUserBrowsingHistory({
          userId: userStore.profile.userId,
          goodsId: goods.value.id,
          source: route.query.source,
          goodsTitle: goods.value.title,
          goodsTitleEn: goods.value.titleEn,
          goodsPrice: state.price,
          goodsImg: goods.value.images[0],
          goodsUrl: goods.value.detailUrl,
          uptime: Date.now(),
          device: 'pc',
        })
      }

      // 没有邮费
      if (!res.postFee) {
        // 可能没有获取到邮费
        const sku = res.skus[0]
        getPostageData(sku)
      } else {
        state.postageCny = res.postFee
        state.postage = flatRateOrigin(res.postFee, userStore.rate)
      }
    }

    state.loading = false
  }
}
// 默认购买动作
const buyAction = (type = 'add') => {
  state.isRead = true
  if (type === 'add') {
    insertCart()
  } else {
    handleNowBuy()
  }
}

const getPostageData = async (sku) => {
  try {
    state.postageLoad = true
    state.actionLoad = true
    const params = {
      itemId: $route.query.id,
      source: $route.query.source,
      skuInfos: [
        {
          skuId: sku.thirdSkuId,
          num: num.value,
        },
      ],
    }
    const postage = await getProductPostFee(params)
    state.postageCny = postage
    state.postage = flatRateOrigin(postage, userStore.rate)
  } catch (error) {
    //
  } finally {
    state.postageLoad = false
    state.actionLoad = false
  }
}
// 获取邮费
const getProductPostage = async () => {
  if (state.isCompete) {
    await getPostageData(state.currSku)
  }
}
// 邮费变更回调
const postageChangeCallback = (postage) => {
  state.postageCny = mulFlatRate(postage, userStore.rate)
}

// 选择的数量
const num = ref(1)
const changeSku = async ({ sku, isCompete, resolve }) => {
  state.isCompete = isCompete
  // 记录选择后的sku，可能有数据，可能没有数据{}
  state.currSku = sku

  if (isCompete) {
    num.value = Math.min(sku.quantity, num.value)
    await getProductPostage()
  } else {
    state.postage = flatRateOrigin(goods.value.postFee, userStore.rate)
    state.postageCny = goods.value.postFee
  }
  resolve && resolve()
}
// 提供goods数据给后代组件使用
provide('goods', goods)
// 生成商品数据
const generateProductParams = () => {
  const { skuId, attr, attrCn, quantity, price, writePrice, promotionPrice, img } = state.currSku
  const { value: good } = goods
  return {
    storeId: good.shopId,
    storeName: good.sellerInfo.shopName,
    storeUrl: good.sellerInfo.shopUrl,
    seller: good.sellerInfo.shopName,
    goodsUrl: good.detailUrl,
    goodsImg: img || good.images[0],
    goodsId: good.id,
    goodsName: userStore.language === 'cn' ? good.title : good.titleEn,
    goodsNameCn: good.title,
    goodsNum: num.value,
    goodsAttr: userStore.language === 'cn' ? attrCn : attr,
    goodsAttrCn: attrCn,
    storageNo: userStore.profile.storageId,
    userId: userStore.profile.id,
    postage: state.postageCny,
    skuId: state.noSku ? undefined : skuId,
    fare: state.postageCny,
    quantity,
    preSell: 0,
    orderDiscountId: 0,
    itemDiscountId: 0,
    writePrice: writePrice || promotionPrice || price,
    goodsPrice: promotionPrice || price,
    actPrice: promotionPrice || price,
    goodsComment: state.goodsComment,
    comment: state.goodsComment,
    tags: null,
    noReason7DReturn: false,
    dataSource: state.noSku ? 'customize' : good.source.toLowerCase(),
    thirdProviderType: state.noSku ? 'customize' : sourceStoreMap[good.source],
    storeSource: sourceStoreMap[good.source],
    minNum: good.startQuantity,
    from: $route.query.from || 'search',
  }
}
// 打开协议
const checkAgree = () => {
  userStore.setDisclaimerAgree(state.isRead)
}
// 前置操作
const actionBeforeCondition = (type) => {
  // 汇率请求中禁止操作
  if (userStore.rateLoading) return
  state.type = type
  // 没有登陆
  if (!userStore.isLogin) {
    $router.push({
      name: 'login',
      query: { redirectUrl: `${location.pathname}${location.search}` },
    })
    return
  }
  if (!userStore.profile.hasActivated) {
    verifyEmailRef.value.openModal()
    return
  }
  if (!state.isRead) {
    ElMessage.warning(t('form.verifyAccept'))
    return
  }
  if (!state.isCompete) {
    ElMessage({ type: 'warning', message: t('product.selectSize') })
    return
  }
  if (goods.value.blackStore) {
    riskReminderDialogRef.value.openModal(1)
    return
  } else {
    if (goods.value.riskCheck) {
      // if (goods.value.action === 0) {
      //   riskReminderDialogRef.value.openModal(2);
      //   return;
      // } else if (goods.value.action === 1) {
      //   riskReminderDialogRef.value.openModal(3);
      //   return;
      // }
    }
  }

  // 需要判断最小起批量
  if (type === 'buy') {
    if (goods.value.startQuantity > num.value) {
      state.showMinNumErr = true
      return
    } else {
      state.showMinNumErr = false
    }
  }
  return true
}
// 加入购物车
const insertCart = async () => {
  if (!actionBeforeCondition('add')) return

  state.actionLoad = true
  try {
    const params = {
      selected: 1,
      categoryId: goods.value.categoryId || '1800273885790212',
      ...generateProductParams(),
    }
    await cartStore.addProduct(params)
    state.goodsComment = ''
    ElMessage({ type: 'success', message: t('cart.addShopSuccess') })

    addCartPoint({
      fb: {
        content_ids: [params.goodsId],
        content_type: 'product',
        value: params.writePrice * params.goodsNum + params.fare,
        currency: 'CNY',
      },
      tt: {
        content_ids: [params.goodsId],
        content_type: 'product',
        price: params.writePrice,
        value: params.writePrice * params.goodsNum + params.fare,
        currency: 'CNY',
        quantity: params.goodsNum,
      },
      gtag: {
        currency: 'CNY',
        value: params.writePrice * params.goodsNum + params.fare, // 商品总价值
        send_to: 'G-KWYFYH5B8P',
        items: [
          {
            item_id: params.skuId,
            item_name: params.goodsName,
            price: params.writePrice,
            quantity: params.goodsNum,
          },
        ],
      },
    })
  } catch (error) {
    ElMessage({ type: 'error', message: error || t('cart.addShopFail') })
  } finally {
    state.actionLoad = false
  }
}
// 添加购物车埋点
const addCartPoint = (params) => {
  try {
    if (process.env.NODE_ENV === 'production') {
      window.gtag('event', 'add_to_cart', params.gtag)
    }
  } catch (error) {
    error
  }
}
// 立即购买
const handleNowBuy = async () => {
  if (!actionBeforeCondition('buy')) return
  state.actionLoad = true
  try {
    const params = generateProductParams()
    const res = await nowBuyProduct(params)
    orderStore.saveShopProductList(res)
    console.log(params.from)
    $router.push({ name: 'order', query: { type: 'buy', from: params.from } })
  } catch (error) {
    ElMessage.error(error)
  } finally {
    state.actionLoad = false
  }
}
// 设置预览图片
const setPreviewImage = (imgUrl) => {
  state.currentImg = imgUrl
}
// 获取分享链接
// 监听上下滑动
const watchYEvent = throttle(() => {
  if (goodsInfoRef.value && !state.goodsInfoHeight) {
    state.goodsInfoHeight = goodsInfoRef.value?.scrollHeight
  }
  if (y.value > goodsInfoRef.value?.scrollHeight) {
    state.isFloat = true
  } else {
    state.isFloat = false
  }
}, 100)
// 监听左右滑动
const watchXEvent = debounce(() => {
  const offsetLeft = width.value - (goodsPagRef.value?.clientWidth || 0)
  document.documentElement.style.setProperty(
    '--page-offset-left',
    `-${offsetLeft > 0 ? Math.round(offsetLeft / 2) + 10 : 10}px`,
  )
}, 100)

onMounted(() => {
  watchXEvent()
})
watch(() => y.value, watchYEvent)
watch(() => width.value, watchXEvent)

// 生成商品类型展示数据
function integrateProductTypeData(data) {
  const nameKey = userStore.language === 'cn' ? 'prop_name' : 'prop_name_en'
  const valKey = userStore.language === 'cn' ? 'value_name' : 'value_name_en'
  const nameKeyEn = 'prop_name_en'
  const valKeyEn = 'value_name_en'
  const nameKeyCn = 'prop_name'
  const valKeyCn = 'value_name'
  const result = []
  let isHasValueId

  data.forEach((sku, index) => {
    if (index === 0) {
      // 有促销价 且 非1688
      if (sku.promotionPrice) {
        state.promotionPrice = sku.promotionPrice
      } else {
        state.price = sku.price
      }
    }
    // 有促销价的情况
    if (state.promotionPrice) {
      state.promotionPrice = Math.min(state.promotionPrice, sku.promotionPrice)
      if (state.promotionPrice === sku.promotionPrice) {
        // 当前价 为最小的 需要复制原价
        state.price = sku.price
      }
    } else {
      state.price = Math.min(state.price, sku.price)
      sku.promotionPrice = 0
    }
    const propArr = sku?.propArr || []

    propArr.forEach((subProp, i) => {
      const skuObj = {
        // 每个sku 需要保存的数据
        id: subProp.value_id,
        name: subProp[valKey],
        inventory: sku.quantity,
        quantity: sku.quantity,
        skuId: sku.skuId,
        thirdSkuId: sku.thirdSkuId,
        disabled: sku.quantity < 1,
        img: sku.picUrl,
        price: sku.price,
        writePrice: sku.promotionPrice || sku.price,
        promotionPrice: sku.promotionPrice,
        attr: `${subProp[nameKeyEn]}:${subProp[valKeyEn]}`,
        attrCn: `${subProp[nameKeyCn]}:${subProp[valKeyCn]}`,
      }
      skuObj.picUrl = sku.picUrl
      if (isHasValueId === undefined) {
        isHasValueId = Boolean(skuObj.id)
      }
      skuObj.id = isHasValueId ? skuObj.id : skuObj.name
      subProp.value_id = skuObj.id

      if (index === 0) {
        // 添加规格分类（颜色 、尺码）
        result[i] = {
          id: subProp.value_id,
          name: subProp[nameKey],
          list: [skuObj],
        }
      } else {
        // 当前分类添加规格
        if (result[i] && result[i].list) {
          const currentTypeList = result[i].list.map((item) => item.id)
          if (!currentTypeList.includes(subProp.value_id)) {
            result[i].list.push(skuObj)
          }
        }
      }
    })
  })
  if (result.length === 0) {
    const skuItem = data[0] || {}
    skuItem.inventory = skuItem.quantity
    state.currSku = skuItem
    skuItem.writePrice = skuItem.promotionPrice || skuItem.price
    state.isCompete = true
    state.noSku = true
  }

  let showImgIndex = undefined
  let defaultIndex = undefined
  let prevSize = 0
  result.forEach((item, index) => {
    let imgUrlSet = new Set([])
    item?.list.forEach((sku) => {
      sku.picUrl && imgUrlSet.add(sku.picUrl)
    })
    if (item?.list.length > 1 && imgUrlSet.size === item?.list.length) {
      typeof showImgIndex !== 'number' && (showImgIndex = index)
    }
    if (imgUrlSet.size) {
      if (item?.list.length > 1 && result.length > 1) {
        if (imgUrlSet.size > 1 && imgUrlSet.size > prevSize) {
          defaultIndex = index
          prevSize = imgUrlSet.size
        }
      } else {
        if (imgUrlSet.size > prevSize) {
          defaultIndex = index
          prevSize = imgUrlSet.size
        }
      }
    }
  })
  typeof showImgIndex !== 'number' && (showImgIndex = defaultIndex)

  goods.value.showImgIndex = showImgIndex
  return result
}
const collectExistsFun = async () => {
  if (!userStore.isLogin) return
  const res = await collectExists({
    goodsId: goods.value.id,
  })
  state.isStar = res
}
const shareClickFun = () => {
  const params = {
    itemId: $route.query.id,
    source: $route.query.source,
    userId: userStore.profile.id,
  }
  getProductShareLink(params).then((res) => {
    state.shareLink = res.url
  })
  shareAffiliateDialogRef.value.openModal()
}
const collectGoodsClickFun = throttle(async () => {
  if (!state.isStar) {
    let params = {
      goodsId: goods.value.id,
      goodsTitle: goods.value[titleKey.value],
      goodsTitleCn: goods.value.title,
      goodsPrice: state.currSku.price,
      goodsImg: goods.value.images[0],
      goodsUrl: goods.value.detailUrl,
      storeSource: goods.value.source,
      storeId: goods.value.shopId,
      deviceType: 'pc',
    }
    await collectAdd(params)
    ElMessage.success(t('common.collectSuccess'))
    state.isStar = true
  } else {
    let params = {
      goodsId: goods.value.id,
      userId: userStore.profile.id,
    }
    await collectRemove(params)
    ElMessage.success(t('common.cancelCollectSuccess'))
    state.isStar = false
  }
}, 1000)

const titleKey = computed(() => (userStore.language === 'cn' ? 'title' : 'titleEn'))
const getGoodsPhotos = async () => {
  let res = await goodsPhotos({ goodsId: goods.value.id })
  state.photosList = res.data
  nextTick(() => {
    state.goodsInfoHeight = 0

    watchYEvent()
  })
}
const getMeasureBySkuIdFun = async () => {
  const { data } = await getMeasureBySkuId({
    spuIds: goods.value.id,
  })
  if (data?.length) {
    state.weight = data[0].weight
    state.volume = data[0].volume
  } else {
    state.weight = undefined
    state.volume = undefined
  }
}
const getItemLogisticsDataFun = async () => {
  const { data } = await getItemLogisticsData({
    goodsId: goods.value.id,
  })
  state.receiveDays = data.receiveDays
}
watch(
  () => userStore.rate,
  (rate) => {
    state.postage = flatRateOrigin(state.postageCny, rate)
  },
)

watch(
  () => route.query.id,
  async () => {
    getProductData()
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
.goods-page-container {
  background-color: var(--white-color-1);
}

.goods-page {
  width: 1320px;
  margin: 0 auto;
  padding-top: 40px;
  position: relative;
}

.goods-info {
  min-height: 640px;
  background: var(--white-color-1);
  display: flex;
  position: relative;

  .media {
    min-width: 640px;
    width: 640px;
    margin-right: 40px;

    .goods-page {
      width: 1320px;
      margin: 0 auto;
      padding-top: 40px;
      position: relative;
    }

    .goods-info {
      min-height: 640px;
      background: var(--white-color-1);
      display: flex;
      position: relative;

      .media {
        min-width: 640px;
        margin-right: 40px;
      }

      .good-content {
        min-width: 640px;
        z-index: 100;
        position: relative;
      }
    }

    .goods-footer {
      display: flex;
      width: 1320px;
      margin-top: 20px;

      .goods-article {
        width: 940px;
        margin-right: 20px;
      }

      .goods-aside {
        width: 280px;
        min-height: 1000px;
      }
    }

    .btn-list {
      margin-top: 28px;

      .buy-btn,
      .add-btn {
        height: 60px;
        margin-right: 43px;
        padding: 15px 44px;
        font-family: Montserrat;
        font-size: 18px;
        font-weight: bold;
        line-height: 30px;
        text-transform: capitalize;
        letter-spacing: 0em;
        border-radius: 8px;
      }

      .buy-btn {
        background-color: var(--primary-color-a5);
        border: none;
        color: var(--white-color-1);
      }

      .add-btn {
        border: 1px solid var(--primary-color-a5);
        color: var(--primary-color);
        background-color: var(--white-color-1);
      }
    }
  }
}

.goods-footer {
  display: flex;
  width: 1320px;
  margin-top: 20px;

  .goods-article {
    width: 940px;
    margin-right: 20px;
  }

  .goods-aside {
    width: 280px;
    min-height: 1000px;
  }
}

.btn-list {
  margin-top: 28px;

  .buy-btn,
  .add-btn {
    height: 60px;
    margin-right: 43px;
    padding: 15px 44px;
    font-family: Montserrat;
    font-size: 18px;
    font-weight: bold;
    line-height: 30px;
    text-transform: capitalize;
    letter-spacing: 0em;
    border-radius: 8px;
  }

  .buy-btn {
    background-color: var(--primary-color-a5);
    border: none;
    color: var(--white-color-1);
  }

  .active-btn {
    background-color: var(--primary-color);
  }

  .add-btn {
    border: 1px solid var(--primary-color-a5);
    color: var(--primary-color);
    background-color: var(--white-color-1);
  }
}

.min-num-err {
  color: red;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: translate(var(--page-offset-left), 60px);
  }

  100% {
    opacity: 1;
    transform: translate(var(--page-offset-left), 0px);
  }
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out 0.1s 1 forwards;
}

.read-agree {
  position: relative;

  ::v-deep {
    .el-checkbox__label {
      font-size: 12px;
    }
  }

  .cover {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    cursor: pointer;
  }
}

.float-goods-content {
  opacity: 0;
  width: 540px;
  // height: 80vh;
  position: fixed;
  top: 150px;
  right: 0;
  background-color: var(--white-color-1);
  // overflow-y: auto;
  overflow: hidden;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0px 8px 24px 0px rgba(149, 157, 165, 0.2);
  border: 1px solid var(--border-da);
  z-index: 100;

  ::v-deep(.item) {
    font-size: 12px;
  }

  ::v-deep(.arrow) {
    margin: 0 5px 0;
  }

  ::v-deep(.goods-data) {
    display: none;
  }

  ::v-deep(.float-goods-data) {
    display: flex;
  }

  ::v-deep(.goods-sku) {
    .list {
      max-height: 220px;
      overflow-y: auto;
    }
  }

  .sku-content {
    // position: sticky;
    // top: 20px;
    max-height: calc(80vh - 120px);
    overflow-y: auto;
    margin-top: 15px;
    padding-bottom: 40px;
  }

  .read-agree {
    margin-top: 12px;
  }

  .btn-list {
    display: flex;
    margin-top: 24px;
    position: absolute;
    z-index: 100;
    bottom: 0px;
    left: 0px;
    background-color: var(--white-color-1);
    padding: 0px 20px 19px;
    width: 100%;

    .buy-btn,
    .add-btn {
      width: 50%;
      height: 44px;
      margin-right: 0;
      margin-left: 0;
    }

    .buy-btn {
      border-radius: 8px 0px 0px 8px;
    }

    .add-btn {
      border-radius: 0px 8px 8px 0px;
    }
  }
}
</style>
